import { create } from 'zustand'
import type { CurrentUser } from '@/types/auth'
import { STORAGE_KEYS } from '@/config/env'

// Auth state (amendment A2):
//  - access token: MEMORY ONLY (never persisted) → refreshed on every reload.
//  - refresh token: persisted in localStorage (accepted, inherited XSS risk — see SECURITY_NOTES.md).
//  - user: cached in localStorage for instant first paint, but re-validated via GET /auth/me on boot.

export type AuthStatus = 'unknown' | 'authenticating' | 'authenticated' | 'anonymous'

interface AuthState {
  status: AuthStatus
  accessToken: string | null
  user: CurrentUser | null
  logoutReason: string | null
  setSession: (access: string, refresh: string, user: CurrentUser) => void
  setTokens: (access: string, refresh: string) => void
  setAccessToken: (access: string) => void
  setUser: (user: CurrentUser) => void
  getRefreshToken: () => string | null
  setStatus: (s: AuthStatus) => void
  forceLogout: (reason?: string) => void
  logout: () => void
}

function readUserCache(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.userCache)
    return raw ? (JSON.parse(raw) as CurrentUser) : null
  } catch {
    return null
  }
}

function clearPersisted(): void {
  localStorage.removeItem(STORAGE_KEYS.refreshToken)
  localStorage.removeItem(STORAGE_KEYS.userCache)
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  accessToken: null,
  user: readUserCache(),
  logoutReason: null,

  setSession: (access, refresh, user) => {
    localStorage.setItem(STORAGE_KEYS.refreshToken, refresh)
    localStorage.setItem(STORAGE_KEYS.userCache, JSON.stringify(user))
    set({ accessToken: access, user, status: 'authenticated', logoutReason: null })
  },

  setTokens: (access, refresh) => {
    localStorage.setItem(STORAGE_KEYS.refreshToken, refresh)
    set({ accessToken: access })
  },

  // Update only the in-memory access token (used by /auth/refresh, which does NOT rotate the refresh token).
  setAccessToken: (access) => set({ accessToken: access }),

  setUser: (user) => {
    localStorage.setItem(STORAGE_KEYS.userCache, JSON.stringify(user))
    set({ user, status: 'authenticated' })
  },

  getRefreshToken: () => localStorage.getItem(STORAGE_KEYS.refreshToken),

  setStatus: (s) => set({ status: s }),

  forceLogout: (reason) => {
    clearPersisted()
    set({ accessToken: null, user: null, status: 'anonymous', logoutReason: reason ?? null })
  },

  logout: () => {
    clearPersisted()
    set({ accessToken: null, user: null, status: 'anonymous', logoutReason: null })
  },
}))
